import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import ProfilePicture from '../ProfilePicture/ProfilePicture';
import UnlockedAchievements from './UnlockedAchievements';
import Badge from '../Badge/Badge';
import BadgeSelector from '../Badge/BadgeSelector';
import apiService from '../../services/api';
import './Profile.scss';

const Profile = () => {
  const { user, isAuthenticated, loading, updateUser } = useAuth();
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showBadgeSelector, setShowBadgeSelector] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [bio, setBio] = useState('');
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioText, setBioText] = useState('');
  const [bioSaving, setBioSaving] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchSummary();
      // Set badge from user data
      if (user.badge) {
        setSelectedBadge(user.badge);
      }
      // Set bio from user data
      setBio(user.bio || '');
      setBioText(user.bio || '');
    }
  }, [isAuthenticated, user]);

  const fetchSummary = async () => {
    try {
      const response = await apiService.get(`/api/game-stats/user/${user.id}/summary`);
      const summaryData = await response.json();
      setSummary(summaryData);
    } catch (error) {
      console.error('Error fetching summary stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditBio = () => {
    setIsEditingBio(true);
    setBioText(bio);
  };

  const handleCancelBio = () => {
    setIsEditingBio(false);
    setBioText(bio);
  };

  const handleSaveBio = async () => {
    setBioSaving(true);
    try {
      const response = await apiService.updateBio(user.id, bioText);
      const data = await response.json();
      
      if (data.success) {
        setBio(data.bio || '');
        setIsEditingBio(false);
        // Update user context with new bio
        updateUser({ ...user, bio: data.bio });
      }
    } catch (error) {
      console.error('Error updating bio:', error);
      alert('Failed to update bio. Please try again.');
    } finally {
      setBioSaving(false);
    }
  };

  const calculateBioLength = (text) => {
    // Count newlines as 50 characters each
    const newlineCount = (text.match(/\n/g) || []).length;
    const regularCharCount = text.length - newlineCount;
    return regularCharCount + (newlineCount * 50);
  };

  const handleBioChange = (e) => {
    const newText = e.target.value;
    setBioText(newText);
  };

  if (loading || isLoading) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="profile-page">
      <div className="profile-page__container">
        <div className="profile-page__header">
          <div className="profile-page__left-column">
            <ProfilePicture size="large" editable={true} />
            <Badge 
              title={selectedBadge ? selectedBadge.name : "Select Badge"}
              achievement={selectedBadge}
              onClick={() => setShowBadgeSelector(true)}
              isSelectable={true}
            />
            <p className="profile-page__joined">
              Member since {new Date(user.created_at).toLocaleDateString()}
            </p>
            {user.username && (
              <Link 
                to={`/profile/${user.username}`} 
                className="profile-page__public-link"
              >
                View Public Profile
              </Link>
            )}
          </div>

          <div className="profile-page__right-column">
            <h1 className="profile-page__username">{user.username}</h1>
            <div className="profile-page__bio-section">
              <h3 className="profile-page__bio-title">Bio:</h3>
              {!isEditingBio ? (
                <div className="profile-page__bio-display">
                  <p className="profile-page__bio-text">
                    {bio || 'No bio.'}
                  </p>
                  <button 
                    className="profile-page__edit-bio-btn"
                    onClick={handleEditBio}
                  >
                    Edit Bio
                  </button>
                </div>
              ) : (
                <div className="profile-page__bio-edit">
                  <textarea
                    className="profile-page__bio-textarea"
                    value={bioText}
                    onChange={handleBioChange}
                    placeholder="Tell other about yourself. (300 characters max)"
                    rows={4}
                  />
                  <div className="profile-page__bio-controls">
                    <span className={`profile-page__bio-counter ${calculateBioLength(bioText) > 300 ? 'profile-page__bio-counter--over-limit' : ''}`}>
                      {calculateBioLength(bioText)}/300
                    </span>
                    <div className="profile-page__bio-buttons">
                      <button 
                        className="profile-page__bio-btn profile-page__bio-btn--cancel"
                        onClick={handleCancelBio}
                        disabled={bioSaving}
                      >
                        Cancel
                      </button>
                      <button 
                        className="profile-page__bio-btn profile-page__bio-btn--save"
                        onClick={handleSaveBio}
                        disabled={bioSaving || calculateBioLength(bioText) > 300}
                      >
                        {bioSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="profile-page__stats">
          <h2>Game Statistics</h2>
          {summary ? (
            <div className="profile-page__stats-grid">
              <div className="profile-page__stat-card">
                <div className="profile-page__stat-number">{summary.total_games}</div>
                <div className="profile-page__stat-label">Total Games</div>
              </div>
              <div className="profile-page__stat-card">
                <div className="profile-page__stat-number">{summary.total_daily_games}</div>
                <div className="profile-page__stat-label">Daily Games</div>
              </div>
              <div className="profile-page__stat-card">
                <div className="profile-page__stat-number">{summary.overall_best_streak}</div>
                <div className="profile-page__stat-label">Best Streak</div>
              </div>
            </div>
          ) : (
            <div className="profile-page__stats-placeholder">
              <p>Loading your statistics...</p>
            </div>
          )}
        </div>

        <UnlockedAchievements userId={user?.id} />
      </div>
      
      {showBadgeSelector && (
        <BadgeSelector
          userId={user.id}
          currentBadgeId={selectedBadge?.id}
          onSelect={(selectedAchievement) => {
            // Update local state
            setSelectedBadge(selectedAchievement);
            
            // Update user in AuthContext
            updateUser({
              ...user,
              badge: selectedAchievement
            });
          }}
          onClose={() => setShowBadgeSelector(false)}
        />
      )}
    </div>
  );
};

export default Profile;